import { useMemo } from "react";
import { AssetTypes } from "@zapier/utils";
import {
  convertToAssetId,
  FolderTreeItem,
  useBulkZapActionPermissions,
  useIsApprovalRequiredForZaps,
} from "@zapier/client";
import {
  BulkSelectAction,
  useEmitZapBulkActionEvent,
  ZapBulkActionOptions,
  ZapModal,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";

interface Input {
  folder?: FolderTreeItem;
  isVirtualPage?: boolean;
  setCurrentZapModal: (modal: ZapModal) => void;
  selectedZapIds?: string[];
  /** Typed-id ("zap:123") delete state; the raw ids selected in this table are converted. */
  zapDeleteState?: Pick<
    BulkSelectAction,
    "getDisabledState" | "onSelectionChange"
  >;
}

const toTypedZapIds = (ids: string[]) =>
  ids.map((id) => convertToAssetId(AssetTypes.Zap, id));

export function useBulkSelectItems({
  selectedZapIds,
  setCurrentZapModal,
  folder,
  isVirtualPage,
  zapDeleteState,
}: Input) {
  const { getDisabledState, onSelectionChange } = zapDeleteState ?? {};
  const { isApprovalRequired, isLoading } = useIsApprovalRequiredForZaps({
    zapIds: selectedZapIds || [],
  });

  const isDeleteAllowed = !isApprovalRequired && !isLoading;

  const { emit } = useEmitZapBulkActionEvent();

  const { canBulkChangeOwner } = useBulkZapActionPermissions({
    folder,
  });

  const tAction = useTranslations("buttons");

  const bulkSelectItems = useMemo(() => {
    // Virtual pages (trash, pending, etc.) do not expose folder bulk actions.
    if (isVirtualPage) {
      return [];
    }

    return [
      canBulkChangeOwner
        ? {
            id: "zap-change-owner",
            onClick: () => {
              setCurrentZapModal(ZapModal.ChangeOwner);
              emit({ optionSelected: ZapBulkActionOptions.ChangeOwner });
            },
            text: tAction("change-owner"),
            icon: "personSettings",
          }
        : undefined,
      {
        id: "zap-export",
        onClick: () => {
          setCurrentZapModal(ZapModal.Export);
          emit({ optionSelected: ZapBulkActionOptions.Export });
        },
        text: tAction("export-zaps"),
        icon: "actionDownload",
      },
      {
        id: "zap-move-to-folder",
        onClick: () => {
          setCurrentZapModal(ZapModal.Move);
          emit({
            optionSelected: ZapBulkActionOptions.Move,
          });
        },
        text: tAction("move-to-folder"),
        icon: "actionMoveToFolder",
      },
      isDeleteAllowed
        ? {
            id: "zap-delete",
            onClick: () => {
              setCurrentZapModal(ZapModal.Delete);
              emit({
                optionSelected: ZapBulkActionOptions.Delete,
              });
            },
            text: tAction("move-to-trash"),
            icon: "actionTrash",
            getDisabledState: getDisabledState
              ? (ids: string[]) => getDisabledState(toTypedZapIds(ids))
              : undefined,
            onSelectionChange: onSelectionChange
              ? (ids: string[]) => onSelectionChange(toTypedZapIds(ids))
              : undefined,
          }
        : undefined,
    ].filter(Boolean) as BulkSelectAction[];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want this to run when conditions change
  }, [
    canBulkChangeOwner,
    emit,
    isApprovalRequired,
    isDeleteAllowed,
    isVirtualPage,
    selectedZapIds,
    setCurrentZapModal,
    tAction,
    getDisabledState,
    onSelectionChange,
  ]);

  return bulkSelectItems;
}
